import { useState, useEffect, useRef } from 'react';
import SectionShell from './SectionShell';
import useSectionSetup from './useSectionSetup';
import SectionHeader from './SectionHeader';
import { hexToRgba } from '../utils';
import gsap from 'gsap';

function ProgressRing({ value, max = 100, size = 80, accent }) {
    const pct = Math.min(100, (value / max) * 100);
    const r = (size - 8) / 2;
    const circ = 2 * Math.PI * r;
    const offset = circ - (pct / 100) * circ;
    return (
        <svg width={size} height={size} className="transform -rotate-90">
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={hexToRgba(accent, 0.1)} strokeWidth={6} />
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={accent} strokeWidth={6}
                strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 1.5s ease-out' }} />
        </svg>
    );
}

function AnimatedValue({ value, suffix = '', visible }) {
    const ref = useRef(null);
    const numRef = useRef({ val: 0 });
    useEffect(() => {
        if (!visible || !ref.current) return;
        const target = parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0;
        const hasDecimal = String(value).includes('.');
        gsap.to(numRef.current, {
            val: target, duration: 1.8, ease: 'power2.out',
            onUpdate: () => {
                if (ref.current) ref.current.textContent = (hasDecimal ? numRef.current.val.toFixed(1) : Math.round(numRef.current.val).toLocaleString()) + suffix;
            }
        });
        return () => gsap.killTweensOf(numRef.current);
    }, [visible, value, suffix]);
    return <span ref={ref}>0{suffix}</span>;
}

export default function ImpactMetricsSection({ block = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { s, ref, visible, anim, config, content } = useSectionSetup(block, { primaryColor, secondaryColor, enableAnimations });
    const items = content.items || [];
    const variant = config.variant || 'cards';

    if (variant === 'rings') {
        return (
            <SectionShell s={s} config={config} anim={anim} ref={ref}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                    {items.map((item, i) => (
                        <div key={i} className="flex flex-col items-center text-center">
                            <div className="relative mb-3">
                                <ProgressRing value={parseFloat(item.value) || 0} max={parseFloat(item.max) || 100} accent={s.accent} />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-lg font-bold" style={{ color: s.text }}>
                                        <AnimatedValue value={item.value} suffix={item.suffix || '%'} visible={visible} />
                                    </span>
                                </div>
                            </div>
                            <h4 className="text-sm font-semibold" style={{ color: s.text }}>{item.label}</h4>
                            {item.description && <p className="text-xs mt-1" style={{ color: s.textSub }}>{item.description}</p>}
                        </div>
                    ))}
                </div>
            </SectionShell>
        );
    }

    // Default: cards
    return (
        <SectionShell s={s} config={config} anim={anim} ref={ref}>
            <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {items.map((item, i) => (
                    <div key={i} className="rounded-2xl border p-6"
                        style={{ borderColor: hexToRgba(s.accent, 0.1) }}>
                        {item.icon && <span className="text-3xl block mb-3">{item.icon}</span>}
                        <div className="text-3xl font-bold mb-1" style={{ color: s.accent }}>
                            {item.prefix}<AnimatedValue value={item.value} suffix={item.suffix || ''} visible={visible} />
                        </div>
                        <h4 className="text-sm font-semibold mb-1" style={{ color: s.text }}>{item.label}</h4>
                        {item.description && <p className="text-xs leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                    </div>
                ))}
            </div>
            {content.footnote && <p className="text-xs text-center mt-6" style={{ color: s.textSub }}>{content.footnote}</p>}
        </SectionShell>
    );
}
